import { useEffect, useRef, useState } from 'react';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from './redux/store';
import { TocItem } from 'components/common/TableOfContents/TableOfContents';

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch: () => AppDispatch = useDispatch;

export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export const useDebouncer = <T extends any[]>(
  callback: (...args: T) => void,
  delay: number = 300
): ((...args: T) => void) => {
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return (...args: T) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  };
};

export const useTableOfContents = (containerId: string, deps: any[] = []) => {
  const [toc, setToc] = useState<TocItem[]>([]);

  useEffect(() => {
    const container = document.getElementById(containerId);
    if (!container) return;

    const headings = Array.from(
      container.querySelectorAll('h1, h2, h3, h4, h5, h6')
    ) as HTMLElement[];

    const items = headings.map((h) => {
      let id =
        h.id ||
        h.innerText
          .toLowerCase()
          .trim()
          .replace(/\s+/g, '-')
          .replace(/[^\w-]/g, '');

      if (!h.id) {
        h.id = id;
      }

      return {
        id,
        text: h.innerText,
        level: parseInt(h.tagName.substring(1), 10),
      };
    });

    setToc(items);
  }, [containerId, ...deps]); // re-run whenever deps change

  return toc;
};
